
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Download, RefreshCw, Trash2, StopCircle, HardDrive } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { downloadBackupFile } from "@/utils/downloadUtils";
import { createFullBackup } from "@/services/backupService";
import { toast } from "@/hooks/use-toast";

interface BackupActionButtonsProps {
  backupId: string;
  websiteId: string;
  status: string;
  downloadUrl: string;
  isDeleting: boolean;
  onDelete: (backupId: string) => void;
  backupType?: 'full' | 'incremental';
}

const BackupActionButtons: React.FC<BackupActionButtonsProps> = ({
  backupId,
  websiteId,
  status,
  downloadUrl,
  isDeleting,
  onDelete,
  backupType
}) => {
  const handleDelete = () => {
    // Call the onDelete function with the backupId
    onDelete(backupId);
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.preventDefault();
    downloadBackupFile(backupId);
  };

  const handleCreateFullBackup = async () => {
    try {
      await createFullBackup(websiteId);
    } catch (error) {
      toast({
        title: "Full backup failed",
        description: "Failed to start full backup. Please try again.",
        variant: "destructive",
      });
    }
  };

  const isInProgress = status === "in-progress";

  return (
    <div className="flex justify-end gap-2">
      {status === "completed" && (
        <>
          <Button variant="outline" size="sm" className="h-8 px-2" onClick={handleDownload}>
            <Download className="h-4 w-4" />
            <span className="sr-only">Download</span>
          </Button>

          <Button size="sm" asChild className="h-8">
            <Link to={`/restore/${websiteId}/${backupId}`}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Restore
            </Link>
          </Button>

          {backupType === 'incremental' && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 px-2">
                  <HardDrive className="h-4 w-4 mr-1" />
                  More
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={handleCreateFullBackup}>
                  <HardDrive className="h-4 w-4 mr-2" />
                  Create Full Backup
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </>
      )}

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={`h-8 px-2 ${isInProgress ? 'text-yellow-600 hover:bg-yellow-50' : 'text-red-600 hover:bg-red-50'}`}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
            ) : isInProgress ? (
              <StopCircle className="h-4 w-4" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            <span className="sr-only">{isInProgress ? "Cancel" : "Delete"}</span>
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{isInProgress ? "Cancel Backup" : "Delete Backup"}</AlertDialogTitle>
            <AlertDialogDescription>
              {isInProgress 
                ? "Are you sure you want to cancel this backup? This action cannot be undone."
                : "Are you sure you want to delete this backup? This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className={isInProgress ? "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500" : "bg-red-600 hover:bg-red-700 focus:ring-red-500"}
            >
              {isInProgress ? "Cancel Backup" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BackupActionButtons;
